import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import ValidatedForm from 'views/Widgets/Forms/ValidatedForm';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';

interface Props {
  editorName: string;
  content: string;
  onContentChange: (value: string) => void;
  isPrivate: boolean;
  onIsPrivateChange: (value: boolean) => void;
  error: string | null;
  isSubmitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel: React.ReactNode;
  placeholder?: string;
  autoFocus?: boolean;
  confirmDiscardOnCancel?: boolean;
  label?: React.ReactNode;
}

const ThreadedCommentComposer: FC<Props> = ({
  editorName,
  content,
  onContentChange,
  isPrivate,
  onIsPrivateChange,
  error,
  isSubmitting,
  onSubmit,
  onCancel,
  submitLabel,
  placeholder,
  autoFocus = false,
  confirmDiscardOnCancel = false,
  label,
}) => {
  const { formatMessage } = useIntl();
  const privateSwitchFieldName = `${editorName}-private`;

  const handleCancel = useCallback(() => {
    if (
      confirmDiscardOnCancel &&
      content !== '' &&
      !window.confirm(
        formatMessage({
          id: 'app.views.widgets.comments.threaded_comment_composer.cancel_unsaved_warning',
          defaultMessage:
            'You have an unsaved comment. Are you sure you want to discard it?',
        })
      )
    ) {
      return;
    }
    onCancel();
  }, [confirmDiscardOnCancel, content, formatMessage, onCancel]);

  const object = useMemo(
    () => ({
      [editorName]: content,
      [privateSwitchFieldName]: isPrivate,
    }),
    [editorName, content, privateSwitchFieldName, isPrivate]
  );

  const inputs = useMemo(
    () => [
      {
        type: INPUT_TYPES.RICH_TEXT_EDITOR,
        name: editorName,
        label: label && <span className="fw-semibold">{label}</span>,
        autoFocus,
        placeholder,
        config: { heightMin: 120 },
      },
      {
        type: INPUT_TYPES.SWITCH,
        name: privateSwitchFieldName,
        helperText: (
          <FormattedMessage
            id="app.views.widgets.comments.threaded_comment_composer.private_label"
            defaultMessage="Make this comment private (only you will be able to see it)"
          />
        ),
      },
    ],
    [editorName, label, autoFocus, placeholder, privateSwitchFieldName]
  );

  const onChange = useCallback(
    (newObject: Record<string, string | boolean>) => {
      const newContent = newObject[editorName] as string;
      if (newContent !== content) {
        onContentChange(newContent || '');
      }
      const newIsPrivate = newObject[privateSwitchFieldName] as boolean;
      if (newIsPrivate !== isPrivate) {
        onIsPrivateChange(newIsPrivate);
      }
    },
    [
      editorName,
      content,
      onContentChange,
      privateSwitchFieldName,
      isPrivate,
      onIsPrivateChange,
    ]
  );

  const callback = useCallback(
    (data?: unknown, error?: unknown) => {
      if (!error && data) {
        onSubmit();
      }
    },
    [onSubmit]
  );

  const renderForm = useCallback(
    (children: React.ReactNode) => (
      <div className="mt-3">
        {children}
        {error && <div className="text-danger small mb-3">{error}</div>}
        <div className="d-flex align-items-center gap-3">
          <Button
            color="primary"
            size="sm"
            type="submit"
            disabled={isSubmitting}
          >
            {submitLabel}
          </Button>
          <Button
            color="link"
            size="sm"
            type="button"
            className="px-0"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            <FormattedMessage
              id="app.views.widgets.comments.threaded_comment_composer.cancel"
              defaultMessage="Cancel"
            />
          </Button>
        </div>
      </div>
    ),
    [error, isSubmitting, submitLabel, handleCancel]
  );

  return (
    <ValidatedForm
      object={object}
      inputs={inputs}
      onChange={onChange}
      callback={callback}
      renderForm={renderForm}
      hideSubmitButton
      isSubmitting={isSubmitting}
      disableUnsavedChangesPrompt
    />
  );
};

export default ThreadedCommentComposer;
